<script setup lang="ts">
import ApplicationLogo from '@/Components/ApplicationLogo.vue'
import ApplicationLogoMobile from '@/Components/ApplicationLogoMobile.vue'
import OutlineButton from '@/Components/Buttons/OutlineButton.vue'
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue'
import MobileMenu from '@/Components/MobileMenu.vue'
import { Link, usePage, router } from '@inertiajs/vue3'
import { onClickOutside } from '@vueuse/core'
import { computed, onMounted, ref, watch } from 'vue'

const user = computed(() => usePage().props.auth.user)
const mobileMenuActive = ref(false)
const dropdownMenuActive = ref(false)
const dropdownMenu = ref(null)
const isMySecrets = computed(() => route('dashboard').endsWith(usePage().url) || isSecret.value)
const isSecret = computed(() => usePage().url.startsWith('/secrets/'))
const isSecretRequest = computed(() => usePage().url.startsWith('/secret-requests'))
const isSecretRequestCreate = computed(() => usePage().url === '/secret-requests/create')

const focusout = ($event?: FocusEvent) => {
    if (typeof document === 'undefined') return

    const dropdownBtn = $event?.currentTarget
    if (dropdownBtn instanceof Node) {
        setTimeout(() => {
            if (!dropdownBtn.contains(document.activeElement)) dropdownMenuActive.value = false
        }, 100)
    }
}

onMounted(() => {
    onClickOutside(dropdownMenu, (event) => (dropdownMenuActive.value = false))

    watch(mobileMenuActive, (value) => {
        if (typeof document === 'undefined') return

        if (value) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'unset'
        }
    })

    router.on('success', (event) => {
        if (mobileMenuActive.value) {
            mobileMenuActive.value = false
        }
    })
})
</script>
<template>
    <header>
        <div
            v-if="false"
            class="bg-primary-600 py-1 text-center text-white"
        >
            sicher-teilen.de neu gestartet: Sparen Sie 20% mit Gutschein-Code „NEUSTART20“ für ein Jahr Pro-Account!
        </div>
        <div class="flex items-center bg-white py-4 pl-3 pr-7">
            <div class="basis-[26%] min-[1750px]:basis-1/4">
                <Link
                    :href="route('home')"
                    class="relative inline-block w-full outline-none transition-transform focus-visible:scale-110"
                    :class="user ? (user.is_subscribed ? 'min-[570px]:max-sm:w-52' : 'min-[610px]:max-sm:w-60') : ''"
                >
                    <ApplicationLogo class="hidden w-[180px] xs:block 2xl:w-[90%] min-[1750px]:w-full" />
                    <ApplicationLogoMobile class="block h-[72px] w-[50px] xs:hidden" />
                    <span class="sr-only">Sicher-Teilen</span>
                    <span
                        v-if="user && user.is_subscribed"
                        class="absolute left-11 top-10 rounded-full bg-primary-500 px-1.5 text-[10px] text-white xxs:left-14 xxs:top-7 xs:left-[176px] sm:left-[182px] sm:text-[12px] 2xl:left-[calc(45%_+_144px)] 2xl:top-6 min-[1750px]:left-[calc(50%_+_144px)]"
                        >PRO</span
                    >
                    <span
                        v-else-if="user"
                        class="absolute left-5 top-14 rounded-full bg-gray-100 px-1.5 text-[10px] xxs:left-14 xxs:top-7 xs:left-[176px] sm:left-[182px] sm:text-[12px] 2xl:left-[calc(45%_+_144px)] 2xl:top-6 min-[1750px]:left-[calc(50%_+_144px)]"
                        >ESSENTIAL</span
                    >
                </Link>
            </div>
            <div class="hidden basis-[52%] justify-center 2xl:flex min-[1750px]:basis-1/2">
                <div
                    class="flex space-x-1 self-center min-[1750px]:space-x-4"
                    role="menu"
                >
                    <template v-if="!user">
                        <Link
                            href="/#Sicherheitsvorteile"
                            class="rounded-3xl px-3 py-2 text-gray-900 underline decoration-transparent outline-none transition-all duration-300 hover:bg-black/5 hover:text-primary-500 hover:decoration-current focus:bg-black/5 focus:text-primary-500 focus:decoration-current"
                            role="menuitem"
                        >
                            Ihre Sicherheitsvorteile
                        </Link>
                        <Link
                            href="/#SoEinfachFunktionierts"
                            class="rounded-3xl px-3 py-2 text-gray-900 underline decoration-transparent outline-none transition-all duration-300 hover:bg-black/5 hover:text-primary-500 hover:decoration-current focus:bg-black/5 focus:text-primary-500 focus:decoration-current"
                            role="menuitem"
                        >
                            So einfach funktioniert's
                        </Link>
                        <Link
                            href="/#Preise"
                            class="rounded-3xl px-3 py-2 text-gray-900 underline decoration-transparent outline-none transition-all duration-300 hover:bg-black/5 hover:text-primary-500 hover:decoration-current focus:bg-black/5 focus:text-primary-500 focus:decoration-current"
                            role="menuitem"
                        >
                            Preise
                        </Link>
                        <Link
                            href="/#FAQ"
                            class="rounded-3xl px-3 py-2 text-gray-900 underline decoration-transparent outline-none transition-all duration-300 hover:bg-black/5 hover:text-primary-500 hover:decoration-current focus:bg-black/5 focus:text-primary-500 focus:decoration-current"
                            role="menuitem"
                        >
                            Häufige Fragen (FAQ)
                        </Link>
                    </template>
                    <template v-if="user && !user.is_subscribed">
                        <Link
                            :href="route('secrets.create')"
                            class="rounded-3xl px-2 py-2 text-gray-900 underline decoration-transparent outline-none transition-all duration-300 hover:bg-black/5 hover:text-primary-500 hover:decoration-current focus:bg-black/5 focus:text-primary-500 focus:decoration-current min-[1750px]:px-3"
                            role="menuitem"
                        >
                            Neues Geheimnis erstellen
                        </Link>
                        <Link
                            :href="route('dashboard')"
                            class="rounded-3xl px-2 py-2 text-gray-900 outline-none transition-all duration-300 hover:bg-black/5 focus:bg-black/5 min-[1750px]:px-3"
                            :class="
                                isMySecrets
                                    ? 'text-primary-500 underline'
                                    : 'underline decoration-transparent hover:text-primary-500 hover:decoration-current focus:text-primary-500 focus:decoration-current'
                            "
                            role="menuitem"
                        >
                            Meine Geheimnisse
                        </Link>
                        <Link
                            href="/#Preise"
                            class="rounded-3xl px-2 py-2 text-gray-900 underline decoration-transparent outline-none transition-all duration-300 hover:bg-black/5 hover:text-primary-500 hover:decoration-current focus:bg-black/5 focus:text-primary-500 focus:decoration-current min-[1750px]:px-3"
                            role="menuitem"
                        >
                            Mehr erreichen mit Pro
                        </Link>
                        <Link
                            href="/#Preise"
                            class="rounded-3xl px-2 py-2 text-gray-900 underline decoration-transparent outline-none transition-all duration-300 hover:bg-black/5 hover:text-primary-500 hover:decoration-current focus:bg-black/5 focus:text-primary-500 focus:decoration-current min-[1750px]:px-3"
                            role="menuitem"
                        >
                            Preise
                        </Link>
                        <Link
                            href="/#FAQ"
                            class="rounded-3xl px-2 py-2 text-gray-900 underline decoration-transparent outline-none transition-all duration-300 hover:bg-black/5 hover:text-primary-500 hover:decoration-current focus:bg-black/5 focus:text-primary-500 focus:decoration-current min-[1750px]:px-3"
                            role="menuitem"
                        >
                            Häufige Fragen (FAQ)
                        </Link>
                    </template>
                    <template v-if="user && user.is_subscribed">
                        <Link
                            :href="route('secrets.create')"
                            class="rounded-3xl px-3 py-2 text-gray-900 underline decoration-transparent outline-none transition-all duration-300 hover:bg-black/5 hover:text-primary-500 hover:decoration-current focus:bg-black/5 focus:text-primary-500 focus:decoration-current"
                            role="menuitem"
                        >
                            Neues Geheimnis erstellen
                        </Link>
                        <Link
                            :href="route('dashboard')"
                            class="rounded-3xl px-3 py-2 text-gray-900 outline-none transition-all duration-300 hover:bg-black/5 focus:bg-black/5"
                            :class="
                                isMySecrets
                                    ? 'text-primary-500 underline'
                                    : 'underline decoration-transparent hover:text-primary-500 hover:decoration-current focus:text-primary-500 focus:decoration-current'
                            "
                            role="menuitem"
                        >
                            Meine Geheimnisse
                        </Link>
                        <Link
                            :href="route('secret-requests.index')"
                            class="rounded-3xl px-3 py-2 text-gray-900 outline-none transition-all duration-300 hover:bg-black/5 focus:bg-black/5"
                            :class="
                                isSecretRequest
                                    ? 'text-primary-500 underline'
                                    : 'underline decoration-transparent hover:text-primary-500 hover:decoration-current focus:text-primary-500 focus:decoration-current'
                            "
                            role="menuitem"
                        >
                            Anforderungslinks
                        </Link>
                        <Link
                            href="/#FAQ"
                            class="rounded-3xl px-3 py-2 text-gray-900 underline decoration-transparent outline-none transition-all duration-300 hover:bg-black/5 hover:text-primary-500 hover:decoration-current focus:bg-black/5 focus:text-primary-500 focus:decoration-current"
                            role="menuitem"
                        >
                            Häufige Fragen (FAQ)
                        </Link>
                    </template>
                </div>
            </div>
            <div
                class="flex basis-full content-center justify-end space-x-1 self-center justify-self-end 2xl:basis-[22%] min-[1750px]:basis-1/4 min-[1750px]:space-x-4"
            >
                <template v-if="!user">
                    <OutlineButton
                        :href="route('login')"
                        as="Button"
                        icon="fa-light fa-arrow-right-to-bracket"
                    >
                        Login
                    </OutlineButton>
                    <PrimaryButton
                        class="hidden max-[1749px]:px-3 md:block"
                        :href="route('register')"
                        icon="fa-light fa-user-plus"
                    >
                        Kostenlos registrieren
                    </PrimaryButton>
                </template>
                <template v-else>
                    <OutlineButton
                        ref="dropdownMenu"
                        :icon="dropdownMenuActive ? 'fa-regular fa-angle-up' : 'fa-regular fa-angle-down'"
                        as="button"
                        class="relative min-w-40 max-md:px-4 max-sm:px-2 2xl:px-2 min-[1650px]:px-5 min-[1820px]:px-10"
                        aria-haspopup="menu"
                        :aria-expanded="dropdownMenuActive ? 'true' : 'false'"
                        @click="dropdownMenuActive = !dropdownMenuActive"
                        @focusout="focusout"
                        @keydown="
                            ($event?: KeyboardEvent) => {
                                const which = $event?.which || $event?.keyCode
                                if (which === 27) dropdownMenuActive = false
                            }
                        "
                    >
                        <i class="fa-solid fa-circle-user mr-2 max-sm:mr-1 2xl:mr-1 min-[1650px]:mr-2"></i>
                        <span
                            class="inline-block overflow-clip whitespace-nowrap max-sm:max-w-24 2xl:max-w-24 min-[1680px]:max-w-28 min-[1820px]:max-w-max"
                        >
                            {{ user.name ?? user.email.split('@')[0] }}
                        </span>
                        <div
                            v-if="dropdownMenuActive"
                            class="absolute left-0 top-14 z-[9999] w-full rounded-3xl bg-white px-2 py-3 text-left shadow-default"
                            role="menu"
                        >
                            <Link
                                :href="route('dashboard')"
                                class="block rounded-3xl px-3 py-2 text-primary-500 outline-none transition-all duration-300 hover:bg-black/5 focus:bg-black/5"
                                role="menuitem"
                            >
                                <i class="fa-regular fa-key"></i>
                                Dashboard
                            </Link>
                            <Link
                                :href="route('profile.edit')"
                                class="block rounded-3xl px-3 py-2 text-primary-500 outline-none transition-all duration-300 hover:bg-black/5 focus:bg-black/5"
                                role="menuitem"
                            >
                                <i class="fa-regular fa-gear"></i>
                                Einstellungen
                            </Link>
                            <a
                                v-if="user.is_subscribed && $page.props.auth.subCreatedAt"
                                :href="route('subscribe.billing-portal')"
                                target="_blank"
                                class="block rounded-3xl px-3 py-2 text-primary-500 outline-none transition-all duration-300 hover:bg-black/5 focus:bg-black/5"
                                role="menuitem"
                            >
                                <i class="fa-regular fa-circle-info"></i>
                                Abrechnung
                            </a>
                            <Link
                                :href="route('logout')"
                                method="post"
                                as="button"
                                class="block w-full rounded-3xl px-3 py-2 text-left text-primary-500 outline-none transition-all duration-300 hover:bg-black/5 focus:bg-black/5"
                                role="menuitem"
                            >
                                <i class="fa-light fa-arrow-right-to-bracket"></i>
                                Logout
                            </Link>
                        </div>
                    </OutlineButton>
                    <PrimaryButton
                        v-if="!user.is_subscribed"
                        href="/#Preise"
                        icon="fa-shart fa-regular fa-rocket-launch"
                        class="max-[1749px]:px-3 max-sm:px-2 max-[610px]:hidden"
                    >
                        Upgrade auf Pro
                    </PrimaryButton>
                </template>
                <button
                    class="2xl:hidden"
                    @click="mobileMenuActive = !mobileMenuActive"
                >
                    <template v-if="!mobileMenuActive">
                        <i class="fa-regular fa-bars text-3xl text-primary-500"></i>
                    </template>
                    <template v-else>
                        <i class="fa-light fa-xmark text-4xl text-primary-500"></i>
                    </template>
                </button>
            </div>
        </div>
        <div
            v-if="isSecret || isSecretRequestCreate"
            class="mx-auto max-w-[85rem] pt-9"
        >
            <OutlineButton
                :href="isSecretRequestCreate ? route('secret-requests.index') : route('dashboard')"
                class="inline-block"
            >
                <i class="fa-regular fa-angle-left mr-2"></i>
                <span class="max-sm:hidden">Zurück</span>
            </OutlineButton>
        </div>
    </header>
    <Transition>
        <div
            v-if="mobileMenuActive"
            class="fixed top-24 z-[999] mt-16 h-[calc(100vh-174px)] w-screen overflow-y-auto bg-white px-3 py-12 xxs:top-20 xxs:mt-14 xxs:h-[calc(100vh-104px)] sm:px-14"
        >
            <div class="flex flex-col place-content-center">
                <MobileMenu />
            </div>
        </div>
    </Transition>
</template>

<style scoped>
.v-enter-active,
.v-leave-active {
    transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}
</style>
